import type { WorkoutSegment, SportType, RunIntensity } from '@/types';

// Normalize strings to ASCII
function normalizeString(str: string): string {
  const map: {[key: string]: string} = {
    'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
    'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N', 'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z'
  };
  return str.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, match => map[match] || match).replace(/[^a-zA-Z0-9 _\-\.]/g, ''); 
}

// Helper: Clean leading dashes/arrows from text lines
function cleanLine(str: string): string {
  if (!str) return "";
  return str.replace(/^[->\s]+/, '').trim();
}

// Helper: Escape XML special characters
function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
}

// FORMAT 1: ZWIFT XML (.zwo)
export const generateZwo = (segments: WorkoutSegment[], name: string, description: string, sport: SportType): string => {
  const sportTag = sport === 'run' ? 'run' : 'bike';
  
  // Clean description line by line to prevent Intervals.icu markdown parsing issues
  // And escape XML characters (especially quotes) to prevent syntax errors
  const cleanDescription = description
    .split('\n')
    .map(line => escapeXml(cleanLine(line)))
    .join('\n');
  
  let xmlBody = '';
  
  // Logic to Group Segments into IntervalsT
  let i = 0;
  while (i < segments.length) {
    const current = segments[i];
    
    // Check for potential Interval Set (must come in pairs: On + Off)
    let grouped = false;

    if (current.type === 'IntervalsT' && (i + 1) < segments.length) {
        const next = segments[i+1];
        
        if (next.type === 'IntervalsT') {
             
             // Look ahead to count repeats
             let repeatCount = 1;
             let j = i + 2; // Start checking after the first pair

             const isMatch = (seg1: WorkoutSegment, seg2: WorkoutSegment) => {
                 return seg1.type === 'IntervalsT' &&
                        seg1.duration === seg2.duration &&
                        Math.abs(seg1.powerLow - seg2.powerLow) < 0.01;
             };

             while (j < segments.length - 1) { // Need at least 2 segments for a pair
                 const checkOn = segments[j];
                 const checkOff = segments[j+1];

                 if (isMatch(checkOn, current) && isMatch(checkOff, next)) {
                     repeatCount++;
                     j += 2;
                 } else {
                     break;
                 }
             }

             if (repeatCount > 1) {
                 // WE FOUND A GROUP!
                 grouped = true;
                 
                 // Use "OnPower" and "OffPower" (0.0 - 2.0 range)
                 xmlBody += `    <IntervalsT Repeat="${repeatCount}" OnDuration="${current.duration}" OffDuration="${next.duration}" OnPower="${current.powerLow.toFixed(2)}" OffPower="${next.powerLow.toFixed(2)}" show_avg="1">\n`;
                 // Use text from the first interval
                 let msg = current.text ? current.text.replace(/\d+\/\d+/, '').trim() : "Intervals";
                 msg = escapeXml(cleanLine(msg)); // Clean leading arrows and escape XML
                 xmlBody += `      <textevent message="${msg}" displaytime="10"/>\n`;
                 xmlBody += `    </IntervalsT>\n`;

                 // Skip the processed segments
                 i += (repeatCount * 2);
             }
        }
    }

    if (!grouped) {
        // Fallback: Render as single tag
        let tag = 'SteadyState';
        if (current.type === 'Warmup') tag = 'Warmup';
        if (current.type === 'Cooldown') tag = 'Cooldown';

        xmlBody += `    <${tag} Duration="${current.duration}" PowerLow="${current.powerLow.toFixed(2)}" PowerHigh="${current.powerHigh.toFixed(2)}">\n`;
        if (current.text) {
             const msg = escapeXml(cleanLine(current.text)); // Clean and escape
             xmlBody += `      <textevent message="${msg}" displaytime="10"/>\n`;
        }
        xmlBody += `    </${tag}>\n`;
        
        i++;
    }
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workout_file>
    <author>Structured Workout Builder AI</author>
    <name>${name}</name>
    <description>${cleanDescription}</description>
    <sportType>${sportTag}</sportType>
    <tags/>
    <workout>
${xmlBody}    </workout>
</workout_file>`;
};

// FORMAT 2: INTERVALS.ICU MARKDOWN
export const generateIntervalsMarkdown = (segments: WorkoutSegment[], sport: SportType, runIntensity: RunIntensity = 'pace'): string => {
    let md = "";
    let i = 0;

    // Helper: Format duration (e.g. 90s, 1m30s, 5m)
    const formatDur = (sec: number): string => {
        if (sec < 60) return `${Math.round(sec)}s`;
        const m = Math.floor(sec / 60);
        const s = Math.round(sec % 60);
        return s > 0 ? `${m}m${s}s` : `${m}m`;
    };

    // Helper: Format Power (e.g. 50%, ramp 50-70%)
    const formatPower = (seg: WorkoutSegment): string => {
        const pLow = Math.round(seg.powerLow * 100);
        const pHigh = Math.round(seg.powerHigh * 100);
        let str = "";
        
        if (Math.abs(pLow - pHigh) > 1) {
            str = `ramp ${pLow}-${pHigh}%`;
        } else {
            str = `${pLow}%`;
        }
        
        // Append unit only if running
        if (sport === 'run') {
            if (runIntensity === 'pace') {
                str += " Pace"; // e.g. "80% Pace"
            } else if (runIntensity === 'hr') {
                str += " HR"; // e.g. "80% HR"
            }
            // If power, we append nothing. "80%" implies Power in Intervals.icu by default.
        }
        return str;
    };

    // Helper: Clean text for headers (remove " 1/5" counters)
    const getCleanText = (txt?: string, fallback = "Intervals") => {
        if (!txt) return fallback;
        return cleanLine(txt).replace(/[\s-]*\d+\/\d+/, '').trim();
    };

    while (i < segments.length) {
        const seg = segments[i];

        // Headers for Major Phases (Optional, mostly for structure)
        if (i === 0 && seg.type === 'Warmup') md += "Warmup\n";
        else if (seg.type === 'Cooldown' && segments[i-1]?.type !== 'Cooldown') md += "\nCooldown\n";
        
        // Loop Detection (Matches logic in ZWO generator)
        let loopCount = 1;
        let isLoop = false;

        if (seg.type === 'IntervalsT' && (i + 1) < segments.length && segments[i+1].type === 'IntervalsT') {
             // Look ahead
             let j = i + 2; 
             const isMatch = (s1: WorkoutSegment, s2: WorkoutSegment) => 
                s1.type === 'IntervalsT' && Math.abs(s1.duration - s2.duration) < 1 && Math.abs(s1.powerLow - s2.powerLow) < 0.01;
             
             while (j < segments.length - 1) {
                 if (isMatch(segments[j], seg) && isMatch(segments[j+1], segments[i+1])) {
                     loopCount++;
                     j += 2;
                 } else {
                     break;
                 }
             }
             if (loopCount > 1) isLoop = true;
        }

        if (isLoop) {
            const onSeg = segments[i];
            const offSeg = segments[i+1];
            const groupName = getCleanText(onSeg.text, "Intervals");
            
            // Header for the loop: "Name<!> Nx"
            md += `\n${groupName}<!> ${loopCount}x\n`;
            // Loop steps
            md += `- ${formatDur(onSeg.duration)} ${formatPower(onSeg)}\n`;
            md += `- ${formatDur(offSeg.duration)} ${formatPower(offSeg)}\n`;
            
            // CRITICAL: Add newline to break the list, ensuring the NEXT segment is not treated as part of the loop
            md += "\n";

            i += (loopCount * 2);
        } else {
            // Single Step
            const name = getCleanText(seg.text, seg.type);
            md += `- ${name}<!> ${formatDur(seg.duration)} ${formatPower(seg)}\n`;
            i++;
        }
    }

    return md.trim();
};

export const generateTcx = (segments: WorkoutSegment[], name: string, sport: SportType): string => {
  const sportStr = sport === 'run' ? 'Running' : 'Biking';
  
  let steps = '';
  segments.forEach(seg => {
    // Intensity: Active or Resting
    let intensity = 'Active';
    const lowerText = (seg.text || "").toLowerCase();
    
    if (seg.type === 'Warmup' || seg.type === 'Cooldown') {
        intensity = 'Active';
    } else if (lowerText.includes('recovery') || lowerText.includes('rest') || lowerText.includes('przerwa') || lowerText.includes('odpoczynek') || lowerText.includes('regeneracja')) {
        intensity = 'Resting';
    }

    // Name/Description
    const rawDesc = seg.text || seg.type;
    const desc = escapeXml(cleanLine(rawDesc)); // Use XML escape here too
    const duration = Math.round(seg.duration);

    // Simplified Step without xsi:type attributes to maximize compatibility
    steps += `<Step><StepId>1</StepId><Duration>${duration}</Duration><Intensity>${intensity}</Intensity><Target><None/></Target><Description>${desc}</Description></Step>`;
  });

  return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2">
<Workouts>
<Workout Sport="${sportStr}">
<Name>${name}</Name>
${steps}
</Workout>
</Workouts>
</TrainingCenterDatabase>`;
};

export const generateMrc = (segments: WorkoutSegment[], name: string): string => {
  // MRC Header needs [COURSE DATA] specifically
  let output = `[COURSE HEADER]
VERSION = 2
UNITS = ENGLISH
DESCRIPTION = ${name}
FILE NAME = ${name}
MINUTES PERCENT
[END COURSE HEADER]
[COURSE DATA]
`;

  let currentTime = 0;
  segments.forEach(seg => {
    const startMin = (currentTime / 60).toFixed(2);
    const startPower = (seg.powerLow * 100).toFixed(0);
    
    output += `${startMin}\t${startPower}\n`;
    
    currentTime += seg.duration;
    
    const endMin = (currentTime / 60).toFixed(2);
    const endPower = (seg.powerHigh * 100).toFixed(0);

    output += `${endMin}\t${endPower}\n`;
  });

  output += `[END COURSE DATA]`;
  return output;
};

export const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = normalizeString(filename).replace(/ /g, '_').toLowerCase();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};